import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, ExternalLink } from 'lucide-react';
import { PageShell, Section, SectionLabel, Card, Divider } from '../../components/elevation';
import { METHOD_PHASES, getMethodPhase, getPhaseBookMeaning } from '../../lib/method/content';

export default function MethodPhasePage() {
  const { phase } = useParams<{ phase: string }>();
  const content = getMethodPhase(phase);

  if (!content) {
    return <Navigate to="/elevation/method" replace />;
  }

  const bookMeaning = getPhaseBookMeaning(content.phase);
  const prev = METHOD_PHASES.find((p) => p.order === content.order - 1);
  const next = METHOD_PHASES.find((p) => p.order === content.order + 1);

  return (
    <PageShell>
      <Section>
        <Link
          to="/elevation/method"
          className="inline-flex items-center gap-1 text-sm text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200 mb-4"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> The Method
        </Link>
        <span className="font-mono text-xs uppercase tracking-wider" style={{ color: content.color }}>
          Phase {content.order} of 5
        </span>
        <h1 className="font-heading font-semibold text-3xl mt-1 mb-2" style={{ color: content.color }}>
          {content.title}
        </h1>
      </Section>

      <Section>
        <SectionLabel>From the book</SectionLabel>
        <Card>
          <p className="text-charcoal-800 dark:text-charcoal-100 leading-relaxed">{bookMeaning}</p>
          <p className="mt-3 text-xs text-charcoal-400 dark:text-charcoal-500">
            — <em>Managing Priorities</em> by Harry Max
          </p>
        </Card>
      </Section>

      <Section>
        <SectionLabel>In Priorities.ai</SectionLabel>
        <Card>
          <p className="text-charcoal-700 dark:text-charcoal-200 leading-relaxed mb-4">
            {content.inTheApp}
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to={content.primaryLink.path}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: content.color }}
            >
              {content.primaryLink.label}
              <ExternalLink className="h-3.5 w-3.5" />
            </Link>
            <Link
              to={content.secondaryLink.path}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-charcoal-300 dark:border-charcoal-600 text-charcoal-700 dark:text-charcoal-200 hover:bg-[var(--hover-tint)] transition-colors"
            >
              {content.secondaryLink.label}
            </Link>
          </div>
        </Card>
      </Section>

      <Section>
        <SectionLabel>Practice it</SectionLabel>
        <Card>
          <Link
            to={content.workbookPath}
            className="flex items-center justify-between gap-4 group"
          >
            <span className="flex items-center gap-2 text-charcoal-700 dark:text-charcoal-200">
              <BookOpen className="h-4 w-4 text-charcoal-400" />
              Work through the interactive {content.title} exercise
            </span>
            <ArrowRight className="h-4 w-4 text-charcoal-400 group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </Card>
      </Section>

      <Divider />

      <Section className="mb-0 flex items-center justify-between">
        {prev ? (
          <Link
            to={`/elevation/method/${prev.phase}`}
            className="inline-flex items-center gap-1 text-sm text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {prev.title}
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link
            to={`/elevation/method/${next.phase}`}
            className="inline-flex items-center gap-1 text-sm text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200"
          >
            {next.title} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        ) : (
          <Link
            to="/elevation/method/toolbox"
            className="inline-flex items-center gap-1 text-sm text-charcoal-500 hover:text-charcoal-700 dark:text-charcoal-400 dark:hover:text-charcoal-200"
          >
            The technique toolbox <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        )}
      </Section>
    </PageShell>
  );
}
